// Settings: Preferences (kept on this device) and Your Account (name, email, log out; account deletion lives in the app).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { getJson } from "@/api/http";
import { Page } from "@/app/Page";
import { GoldRange, Toggle } from "@/kit/city";
import { IconChevronRight } from "@/kit/icons";
import { Confirm } from "@/kit/overlays";
import { Button, Card, CardHead, Disc, Row, RowText, Seg } from "@/kit/primitives";
import { clampTextSize, usePrefs } from "@/state/prefs";
import { usePrompts } from "@/state/prompts";
import { useSession } from "@/state/session";
import { useClock } from "@/state/clock";
import { ValorToolsCard } from "@/kit/ValorToolsCard";

type Tab = "prefs" | "account";

export function Settings() {
  const session = useSession();
  const nav = useNavigate();
  const qc = useQueryClient();
  const prefs = usePrefs();
  const [tab, setTab] = useState<Tab>("prefs");
  const [name, setName] = useState(session.login ?? "");
  const [email, setEmail] = useState(session.email ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [logout, setLogout] = useState(false);
  const [del, setDel] = useState(false);
  const health = useQuery({ queryKey: ["health"], queryFn: () => getJson<{ version?: string; build?: number }>("/api/health"), staleTime: Infinity });
  useEffect(() => setName(session.login ?? ""), [session.login]);
  useEffect(() => setEmail(session.email ?? ""), [session.email]);

  const run = async (label: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(label);
    try {
      await fn();
      usePrompts.getState().toast(ok, "success");
      return true;
    } catch (e) {
      usePrompts.getState().error(e);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const signOut = async () => {
    try { await api.session.logout(); } catch { /* the cookie is gone either way */ }
    useSession.getState().setSignedOut();
    qc.clear();
    nav("/sign-in", { replace: true });
  };

  const pref = (label: string, text: string, key: keyof typeof prefs, soon?: boolean) => (
    <Row>
      <RowText line={<b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>{label}</b>} meta={soon ? `${text} · audio comes later` : text} />
      <div className="right"><Toggle on={!!prefs[key]} label={label} onChange={(on) => prefs.set({ [key]: on } as never)} /></div>
    </Row>
  );

  return (
    <Page layout="col" header={{ kind: "title", title: "SETTINGS", subtitle: `Signed in as ${session.login ?? "you"}` }}>
      <div className="stack">
        {/* Classic Frame (Oct 10 2026): no hero — the header already says Settings and who is signed in */}
        <Seg value={tab} onChange={setTab} options={[{ value: "prefs", label: "Preferences" }, { value: "account", label: "Your account" }]} />

        {tab === "prefs" && (
          <>
            <Card>
              <CardHead title="Display" />
              <div className="rows">
                <Row className="ts-row">
                  <RowText line={<b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>Text size</b>} meta="Bigger or smaller text everywhere in the game" />
                  <div className="right"><span className="val ts-pct" aria-live="polite">{prefs.textSize}%</span></div>
                  <div className="ts-ctl">
                    <Disc size="sm" label="Smaller text" disabled={prefs.textSize <= 85} onClick={() => prefs.set({ textSize: clampTextSize(prefs.textSize - 5) })}><span className="ts-a sm">A</span></Disc>
                    <GoldRange value={prefs.textSize} min={85} max={130} step={5} label="Text size" onChange={(n) => prefs.set({ textSize: clampTextSize(n) })} />
                    <Disc size="sm" label="Bigger text" disabled={prefs.textSize >= 130} onClick={() => prefs.set({ textSize: clampTextSize(prefs.textSize + 5) })}><span className="ts-a lg">A</span></Disc>
                  </div>
                  {prefs.textSize !== 100 && <button type="button" className="link caps ts-reset" onClick={() => prefs.set({ textSize: 100 })}>Reset to 100%</button>}
                </Row>
                <Row className="ts-row cv-row">
                  <RowText line={<b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>City view opens at</b>} meta="Where the city opens · pinch or scroll to zoom from the overview (0.8×) to close up (2.5×)" />
                  <div className="cv-ctl">
                    <Seg value={prefs.cityOpens} onChange={(v) => prefs.set({ cityOpens: v })} options={[
                      { value: "last", label: "Last view" }, { value: "overview", label: "Overview" }, { value: "whole", label: "Whole city" }, { value: "close", label: "Close-up" },
                    ]} />
                  </div>
                </Row>
                <Row className="ts-row cv-row">
                  <RowText line={<b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>City labels</b>} meta="On the buildings at the whole-city view · close up they always show in full" />
                  <div className="cv-ctl">
                    <Seg value={prefs.cityLabels} onChange={(v) => prefs.set({ cityLabels: v })} options={[
                      { value: "full", label: "Full" }, { value: "compact", label: "Compact" }, { value: "level", label: "Level only" },
                    ]} />
                  </div>
                </Row>
                {pref("Classic fonts", "The game's original serif type, smaller text included", "classicFonts")}
                {pref("Banner padding", "Keep the header clear of the notch and status bar", "bannerPadding")}
                {pref("Tight interface", "Tighter spacing so more fits on screen", "tight")}
                {pref("Recruit videos", "Units move on the Barracks, Stable and Workshop · off shows the paintings", "recruitVideo")}
                <Row as="button" onClick={() => usePrompts.getState().toast("English is the only language for now.")}>
                  <RowText line={<b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>Language</b>} meta="Game text and notifications" />
                  <div className="right"><span className="val">English</span><IconChevronRight style={{ width: 16, height: 16, color: "var(--g-text)" }} /></div>
                </Row>
              </div>
            </Card>
            <Card>
              <CardHead title="Sound" />
              <div className="rows">
                {pref("Music", "Background score", "music", true)}
                {pref("Sound effects", "Battles, builds and alerts", "sfx", true)}
              </div>
            </Card>
            <Card>
              <CardHead title="Advanced" />
              <div className="rows">{pref("Show debug overlay", "Server timing and version on screen", "debug")}</div>
            </Card>
            <Card small>
              <Row as="button" onClick={() => nav("/help")} style={{ background: "transparent", border: 0 }}>
                <RowText line={<b className="gold" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".11em", textTransform: "uppercase", fontSize: "var(--fs-h)" }}>Help</b>} />
                <div className="right"><span className="val muted">Guides &amp; support</span><IconChevronRight style={{ width: 16, height: 16, color: "var(--g-text)" }} /></div>
              </Row>
            </Card>
            <div className="c-note" style={{ letterSpacing: ".2em", textTransform: "uppercase", fontStyle: "normal", fontFamily: "var(--caps)", fontSize: "var(--fs-label)" }}>Valor · build {health.data?.build ?? "—"} · {health.data?.version ?? ""}</div>
          </>
        )}

        {tab === "account" && (
          <>
            <Card>
              <CardHead title="Profile" />
              <div className="stack" style={{ gap: "calc(var(--u) * 14)" }}>
                <div className="field">
                  <label htmlFor="s-name">Name</label>
                  <div className="field-row">
                    <input id="s-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={20} autoComplete="username" />
                    <Button size="md" disabled={!name.trim() || name.trim() === session.login} busy={busy === "login"}
                      onClick={async () => { const ok = await run("login", () => api.account.updateLogin(name.trim()), "Name changed."); if (ok) useSession.setState({ login: name.trim() }); }}>Change</Button>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="s-email">Email</label>
                  <div className="field-row">
                    <input id="s-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                    <Button size="md" disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || email === session.email} busy={busy === "email"}
                      onClick={async () => { const ok = await run("email", () => api.account.updateEmail(email.trim()), "Email changed."); if (ok) useSession.setState({ email: email.trim() }); }}>Change</Button>
                  </div>
                </div>
              </div>
            </Card>
            <ValorToolsCard wid={session.wid} />
            <Card>
              <CardHead title="Log out" />
              <p style={{ margin: "0 0 calc(var(--u) * 16)", font: "400 var(--fs-body2)/1.45 var(--text)", color: "var(--cream)" }}>
                <b className="gold">Warning:</b> You can log in and out of this account on this device as often as you like. If you log in to another account on this device, this one is unavailable for <b>14 days</b> on this and every other device.
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}><Button kind="steel" onClick={() => setLogout(true)}>Log out</Button></div>
            </Card>
            <Card className="danger">
              <CardHead title="Delete account" />
              <p style={{ margin: 0, font: "400 var(--fs-body2)/1.45 var(--text)", color: "var(--cream)" }}><b className="crimtxt">Caution!</b> Deleting your account <b>permanently wipes</b> everything stored on our servers:</p>
              <ul className="howlist danger-list"><li>Your villages become barbarian villages</li><li>Your in-game messages and communications are erased</li><li>Your trophies and achievements are lost</li></ul>
              <p className="muted" style={{ margin: "0 0 calc(var(--u) * 14)", font: "400 var(--fs-meta)/1.4 var(--text)" }}>This email address cannot open a new account for <b>14 days</b> on this or any other device.</p>
              <Button kind="crimson" wide onClick={() => setDel(true)}>Delete my account permanently</Button>
            </Card>
          </>
        )}
      </div>
      <DebugOverlay version={health.data?.version} />
      <Confirm open={logout} onClose={() => setLogout(false)} title="Log out?" text="You'll need your email and password to sign back in." action="Log out" kind="steel" onConfirm={signOut} />
      <Confirm open={del} onClose={() => setDel(false)} title="Delete your account" text="Account deletion isn't available on the web yet. Open the Valor app, go to Settings › Your Account and delete it there — the same account, the same 14-day rule." action="OK" kind="steel" onConfirm={() => setDel(false)} />
    </Page>
  );
}

function DebugOverlay({ version }: { version?: string }) {
  const on = usePrefs((s) => s.debug);
  const offset = useClock((s) => s.offsetMs);
  const synced = useClock((s) => s.synced);
  if (!on) return null;
  return (
    <div className="debug-overlay" aria-hidden="true">
      v {version ?? "?"} · clock {synced ? `${offset >= 0 ? "+" : ""}${Math.round(offset)} ms` : "unsynced"} · {typeof navigator !== "undefined" ? `${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio}` : ""}
    </div>
  );
}
